"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Eye, EyeOff, Plus, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

interface OAuth2FormProps {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}

export function OAuth2Form({ config, onChange }: OAuth2FormProps) {
  const [showSecret, setShowSecret] = useState(false);
  const [newParamKey, setNewParamKey] = useState("");
  const [newParamValue, setNewParamValue] = useState("");

  const grantType = (config.grant_type as string) || "client_credentials";
  const tokenAuthMethod =
    (config.token_auth_method as string) || "client_secret_post";
  const extraTokenParams =
    (config.extra_token_params as Record<string, string>) || {};

  const addExtraParam = () => {
    if (!newParamKey.trim()) return;
    const updated = {
      ...extraTokenParams,
      [newParamKey.trim()]: newParamValue,
    };
    onChange({ extra_token_params: updated });
    setNewParamKey("");
    setNewParamValue("");
  };

  const removeExtraParam = (key: string) => {
    const updated = { ...extraTokenParams };
    delete updated[key];
    onChange({ extra_token_params: updated });
  };

  return (
    <div className="space-y-4">
      {/* Grant Type */}
      <div className="space-y-2">
        <Label className="text-white/80">Grant Type</Label>
        <Select
          value={grantType}
          onValueChange={(value) => onChange({ grant_type: value })}
        >
          <SelectTrigger className="border-white/10 bg-white/5 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="client_credentials">
              Client Credentials
            </SelectItem>
            <SelectItem value="authorization_code">
              Authorization Code
            </SelectItem>
            <SelectItem value="password">Password (Resource Owner)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Token URL */}
      <div className="space-y-2">
        <Label className="text-white/80">Token URL</Label>
        <Input
          value={(config.token_url as string) || ""}
          onChange={(e) => onChange({ token_url: e.target.value })}
          placeholder="https://oauth.example.com/token"
          className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
        />
        <p className="text-xs text-white/40">The OAuth2 token endpoint URL</p>
      </div>

      {/* Client ID */}
      <div className="space-y-2">
        <Label className="text-white/80">Client ID</Label>
        <Input
          value={(config.client_id as string) || ""}
          onChange={(e) => onChange({ client_id: e.target.value })}
          placeholder="Your client ID"
          className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
        />
      </div>

      {/* Client Secret */}
      <div className="space-y-2">
        <Label className="text-white/80">Client Secret</Label>
        <div className="relative">
          <Input
            type={showSecret ? "text" : "password"}
            value={(config.client_secret as string) || ""}
            onChange={(e) => onChange({ client_secret: e.target.value })}
            placeholder="Your client secret"
            className="border-white/10 bg-white/5 pr-10 text-white placeholder:text-white/40"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-white/40 hover:text-white"
            onClick={() => setShowSecret(!showSecret)}
          >
            {showSecret ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Scope */}
      <div className="space-y-2">
        <Label className="text-white/80">Scope</Label>
        <Input
          value={(config.scope as string) || ""}
          onChange={(e) => onChange({ scope: e.target.value })}
          placeholder="e.g. https://graph.microsoft.com/.default"
          className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
        />
        <p className="text-xs text-white/40">
          Space-separated scopes. Sent on every token request including refresh.
        </p>
      </div>

      {/* Token Auth Method */}
      <div className="space-y-2">
        <Label className="text-white/80">Token Endpoint Auth Method</Label>
        <Select
          value={tokenAuthMethod}
          onValueChange={(value) => onChange({ token_auth_method: value })}
        >
          <SelectTrigger className="border-white/10 bg-white/5 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="client_secret_post">
              POST Body (default)
            </SelectItem>
            <SelectItem value="client_secret_basic">
              Basic Auth Header
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-white/40">
          How client credentials are sent to the token endpoint
        </p>
      </div>

      {/* Authorization Code fields */}
      {grantType === "authorization_code" && (
        <>
          <div className="space-y-2">
            <Label className="text-white/80">Authorization URL</Label>
            <Input
              value={(config.authorization_url as string) || ""}
              onChange={(e) => onChange({ authorization_url: e.target.value })}
              placeholder="https://oauth.example.com/authorize"
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">Redirect URI</Label>
            <Input
              value={(config.redirect_uri as string) || ""}
              onChange={(e) => onChange({ redirect_uri: e.target.value })}
              placeholder="https://yourapp.com/callback"
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
            />
          </div>
        </>
      )}

      {/* Password fields */}
      {grantType === "password" && (
        <>
          <div className="space-y-2">
            <Label className="text-white/80">Username</Label>
            <Input
              value={(config.username as string) || ""}
              onChange={(e) => onChange({ username: e.target.value })}
              placeholder="Resource owner username"
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">Password</Label>
            <Input
              type="password"
              value={(config.password as string) || ""}
              onChange={(e) => onChange({ password: e.target.value })}
              placeholder="Resource owner password"
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
            />
          </div>
        </>
      )}

      {/* Extra Token Params */}
      <div className="space-y-3">
        <Label className="text-white/80">Extra Token Parameters</Label>
        <p className="text-xs text-white/40">
          Additional params sent on every token request (e.g. resource,
          audience)
        </p>

        {/* Existing params */}
        {Object.entries(extraTokenParams).length > 0 && (
          <div className="space-y-2">
            {Object.entries(extraTokenParams).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                <div className="flex-1 rounded border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white/70">
                  <span className="font-medium text-white/90">{key}</span>
                  <span className="mx-1.5 text-white/60">=</span>
                  <span>{value}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-white/60 hover:text-red-400"
                  onClick={() => removeExtraParam(key)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Add new param */}
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Input
              value={newParamKey}
              onChange={(e) => setNewParamKey(e.target.value)}
              placeholder="Key (e.g. resource)"
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
            />
          </div>
          <div className="flex-1 space-y-1">
            <Input
              value={newParamValue}
              onChange={(e) => setNewParamValue(e.target.value)}
              placeholder="Value"
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addExtraParam();
                }
              }}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 border-white/10 text-white/60 hover:text-white"
            onClick={addExtraParam}
            disabled={!newParamKey.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Quick Setup hint */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-3">
        <p className="text-xs font-medium text-white/70">Quick Setup</p>
        <p className="mt-1 text-xs text-white/50">
          For Microsoft APIs, use:{" "}
          <code className="rounded bg-white/10 px-1 py-0.5">
            https://login.microsoftonline.com/{"{tenant_id}"}/oauth2/v2.0/token
          </code>
        </p>
        <p className="mt-1 text-xs text-white/50">
          For Azure AD v1 endpoints, add{" "}
          <code className="rounded bg-white/10 px-1 py-0.5">resource</code> as
          an extra token parameter.
        </p>
      </div>
    </div>
  );
}
